import { GenderValue } from "shared";
import {
  capitalizeFirstLetter,
  symptomTxt,
} from "../uiControllers/SymptomText";
import { PatientFacingQuestion } from "../../types/patientConversationAliasing";
import {
  MeasurementUnits,
  QualifiedFeature,
  QualifierFragment,
  QualifierValue,
  QuestionChoiceType,
} from "fe-common/graphql";
import {
  ComplexUnitsToString,
  MeasurementUnitsToString,
} from "../../utils/dataHelpers";
import { AnswerText } from "./getAnswerTextByType";
import { numericTimingConfig } from "../Questions/rangeQuestions/numericQuestions/numericTimingConfig";

//region [[ Styles ]]
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SuggestionAnswerProps {
  gender: GenderValue;
  question?: PatientFacingQuestion;
  selectedQfs: QualifiedFeature[];
  translate: (KEY: string) => string;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const toFixedPointIfNeeded = (n: number) =>
  Math.floor(n) === n ? n.toFixed(0) : n.toFixed(2);

const isRangeInUnit = (
  q: QualifierFragment | QualifierValue,
  unit: MeasurementUnits,
) => q.range?.units === unit || q.range?.complexUnits?.numerator?.[0] === unit;

//endregion [[ Functions ]]

export const getSuggestionAnswer = ({
  gender,
  selectedQfs,
  translate,
  question = null,
  ...props
}: SuggestionAnswerProps): AnswerText => {
  const ANSWER_YES = translate("ANSWER_YES");
  const ANSWER_SKIP = translate("ANSWER_SKIP");
  const ANSWER_NONE_OF_THE_ABOVE = translate("ANSWER_NONE_OF_THE_ABOVE");

  const singleRefinementText = (isSingleRefinement) => {
    return isSingleRefinement ? ANSWER_SKIP : ANSWER_NONE_OF_THE_ABOVE;
  };

  const multipleRefinementText = (isSingleRefinement, selectedQfs: any) => {
    return isSingleRefinement
      ? ANSWER_YES
      : selectedQfs.length &&
          selectedQfs
            .flatMap((qf) => qf.qualifiers)
            .map((q) => q.value.shortname)
            .join(", ");
  };

  const singleSuggestionText = (isSingleSuggestion) => {
    return isSingleSuggestion ? ANSWER_SKIP : ANSWER_NONE_OF_THE_ABOVE;
  };

  const multipleSuggestionText = (isSingleSuggestion, selectedQfs: any) => {
    return isSingleSuggestion
      ? ANSWER_YES
      : selectedQfs.map((qf) => symptomTxt(qf)).join(", ");
  };

  const isRefinement =
    question?.suggestions?.length === 1 &&
    question?.suggestions[0]?.refinements?.length;

  const firstSuggestion = question?.suggestions?.[0];
  const firstRelation =
    firstSuggestion?.suggestion?.qualifiers?.[0]?.relation?.id;

  const isSingleRefinement =
    question?.suggestions.length === 1 &&
    question?.suggestions[0].refinements?.length === 1;

  const isSingleSuggestion = question?.suggestions?.length === 1;

  const isNumeric =
    question?.questionChoiceType === QuestionChoiceType.ChooseNumeric;

  const multiQuestionPrefix =
    isNumeric && !!question?.suggestions?.[0]
      ? question?.suggestions[0].suggestion.feature.shortname
      : null;

  let answerText;

  let keepLtr = false;

  const numericConf = numericTimingConfig[firstRelation];
  if (numericConf) {
    return numericConf.answerText(selectedQfs[0], translate);
  }

  if (isNumeric) {
    // For numeric values take the lower value of the range
    answerText =
      selectedQfs.length === 0
        ? ANSWER_SKIP
        : selectedQfs
            .flatMap((qf) =>
              qf.qualifiers.map((q) => (q.value?.range ? q.value : q)),
            )
            .map((q) => {
              if (isRangeInUnit(q, MeasurementUnits.Inches)) {
                const feet = Math.floor(q.range?.low / 12);
                const inches = parseFloat((q.range?.low % 12).toFixed(1));
                return (feet >= 1 ? `${feet}'` : "") + `${inches}"`;
              }
              if (isRangeInUnit(q, MeasurementUnits.Years)) {
                return `${q.range?.low} ${
                  q.range?.low === 1 ? translate("year") : translate("years")
                }`;
              }
              // We have english F or C or % icons so make sure to keep ltr even if we are in hebrew or arabic
              keepLtr =
                isRangeInUnit(q, MeasurementUnits.Celsius) ||
                isRangeInUnit(q, MeasurementUnits.Fahrenheit) ||
                isRangeInUnit(q, MeasurementUnits.Percent);

              const unitAsString = q.range?.units
                ? MeasurementUnitsToString(q.range?.units, translate)
                : ComplexUnitsToString(q.range?.complexUnits, translate);

              return (
                toFixedPointIfNeeded(q.range?.low) +
                (unitAsString !== "" ? ` ${unitAsString}` : "")
              );
            })
            .join("/");
  } else if (isRefinement) {
    answerText =
      selectedQfs.length > 0
        ? multipleRefinementText(isSingleRefinement, selectedQfs)
        : singleRefinementText(isSingleRefinement);
  } else {
    answerText =
      selectedQfs.length > 0
        ? multipleSuggestionText(isSingleSuggestion, selectedQfs)
        : singleSuggestionText(isSingleSuggestion);
  }

  keepLtr = keepLtr || (isNumeric && selectedQfs?.length > 1);

  return {
    text: capitalizeFirstLetter([answerText]),
    isNumeric,
    keepLtr,
    multiQuestionPrefix,
  };
};
